import { useState } from "react";
import { GripVertical, ChevronDown, ChevronUp, RotateCcw, Eye, EyeOff } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { DEFAULT_SECTIONS, SECTION_LABELS, mergeSections } from "@/lib/sections";

/**
 * Editor for the campaign landing page sections.
 * - Drag-to-reorder (HTML5 drag, no extra dep)
 * - Toggle visibility per section
 * - Inline edit of section-specific text fields
 * - Reset to defaults
 *
 * Props:
 *   value:    current sections array (may be empty → defaults are shown)
 *   onChange: (newSectionsArray) => void
 */
export default function SectionsEditor({ value, onChange }) {
  const sections = mergeSections(value);
  const [expanded, setExpanded] = useState(null);
  const [dragId, setDragId] = useState(null);

  const update = (id, patch) => {
    onChange(sections.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const move = (fromId, toId) => {
    if (!toId || fromId === toId) return;
    const arr = [...sections];
    const from = arr.findIndex((s) => s.id === fromId);
    const to = arr.findIndex((s) => s.id === toId);
    if (from < 0 || to < 0) return;
    // Lock hero in place
    if (arr[from].locked || arr[to].locked) return;
    const [picked] = arr.splice(from, 1);
    arr.splice(to, 0, picked);
    onChange(arr);
  };

  const resetDefaults = () => {
    if (window.confirm("לאפס את כל הסקשנים לברירת המחדל? זה ימחק טקסטים מותאמים.")) {
      onChange(DEFAULT_SECTIONS.map((s) => ({ ...s })));
    }
  };

  return (
    <div className="space-y-3" data-testid="sections-editor">
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs text-stone-500 leading-[1.6]">
          גררו את הסקשנים כדי לשנות סדר. השתמשו במתג כדי להסתיר/להציג. לחצו על חץ כדי לערוך טקסטים.
          סקשן ה-Hero תמיד ראשון ולא ניתן להזזה.
        </p>
        <button
          type="button"
          onClick={resetDefaults}
          data-testid="sections-reset-btn"
          className="text-xs font-bold text-stone-500 hover:text-brand-amber flex items-center gap-1 whitespace-nowrap"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          איפוס לברירת מחדל
        </button>
      </div>

      {sections.map((s) => {
        const isOpen = expanded === s.id;
        const enabled = s.enabled !== false;
        const isHero = s.id === "hero";
        return (
          <div
            key={s.id}
            data-testid={`section-card-${s.id}`}
            draggable={!isHero}
            onDragStart={() => !isHero && setDragId(s.id)}
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={() => { move(dragId, s.id); setDragId(null); }}
            onDragEnd={() => setDragId(null)}
            className={`bg-white rounded-2xl border ${dragId === s.id ? "border-brand-amber shadow-md" : "border-stone-200"} ${!enabled ? "opacity-60" : ""} transition`}
          >
            {/* Row header */}
            <div className="flex items-center gap-3 p-3">
              <span
                className={`${isHero ? "text-stone-300 cursor-not-allowed" : "text-stone-400 cursor-grab active:cursor-grabbing"} touch-none`}
                aria-label="גרור"
              >
                <GripVertical className="w-5 h-5" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-[800] text-sm">{SECTION_LABELS[s.id] || s.id}</span>
                  {isHero && <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">נעול · ראשון</span>}
                </div>
                <div className="text-xs text-stone-500 truncate">
                  {s.id === "pain" && (s.title || "—")}
                  {s.id === "explanation" && (s.title_main || "—")}
                  {s.id === "questionnaire" && (s.title || "—")}
                  {s.id === "stats" && "תצוגת נתוני הקמפיין (ערוך ברשימה למעלה)"}
                  {s.id === "bonus" && `${s.title_prefix || ""} ${s.title_accent || ""}`.trim()}
                  {s.id === "final_cta" && (s.title || "—")}
                  {s.id === "hero" && (s.eyebrow || "—")}
                </div>
              </div>
              <button
                type="button"
                onClick={() => update(s.id, { enabled: !enabled })}
                data-testid={`section-toggle-${s.id}`}
                disabled={isHero}
                className={`p-2 rounded-lg ${isHero ? "opacity-30 cursor-not-allowed" : "hover:bg-stone-100"} transition`}
                aria-label={enabled ? "הסתר" : "הצג"}
              >
                {enabled ? <Eye className="w-4 h-4 text-emerald-600" /> : <EyeOff className="w-4 h-4 text-stone-400" />}
              </button>
              <Switch
                checked={enabled}
                onCheckedChange={(v) => update(s.id, { enabled: v })}
                disabled={isHero}
                data-testid={`section-switch-${s.id}`}
                className="data-[state=checked]:bg-brand-amber"
              />
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : s.id)}
                data-testid={`section-expand-${s.id}`}
                className="p-2 rounded-lg hover:bg-stone-100 transition"
                aria-label={isOpen ? "סגור" : "ערוך"}
              >
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>

            {/* Expanded editor */}
            {isOpen && (
              <div className="border-t border-stone-100 p-4 bg-stone-50/50 space-y-3" data-testid={`section-fields-${s.id}`}>
                <SectionFields section={s} onChange={(patch) => update(s.id, patch)} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SectionFields({ section, onChange }) {
  switch (section.type) {
    case "hero":
      return (
        <>
          <FieldRow label="Eyebrow (תווית מעל הכותרת)">
            <Input value={section.eyebrow || ""} onChange={(e) => onChange({ eyebrow: e.target.value })} className="h-10" />
          </FieldRow>
          <FieldRow label="הערת טופס (HTML מוגבל מותר)">
            <Textarea value={section.form_note || ""} onChange={(e) => onChange({ form_note: e.target.value })} rows={2} className="text-xs" />
          </FieldRow>
          <p className="text-xs text-stone-400">* כותרת ראשית, תת-כותרת וטקסטי ה-CTA נערכים בכרטיסיית "תוכן Hero" למעלה.</p>
        </>
      );
    case "pain":
      return (
        <>
          <FieldRow label="Eyebrow"><Input value={section.eyebrow || ""} onChange={(e) => onChange({ eyebrow: e.target.value })} className="h-10" /></FieldRow>
          <FieldRow label="כותרת"><Input value={section.title || ""} onChange={(e) => onChange({ title: e.target.value })} className="h-10" /></FieldRow>
          <p className="text-xs text-stone-400">* בולטי הכאב נערכים בקטע "בולטי כאב" למטה.</p>
        </>
      );
    case "explanation":
      return (
        <>
          <FieldRow label="כותרת ראשית"><Input value={section.title_main || ""} onChange={(e) => onChange({ title_main: e.target.value })} className="h-10" /></FieldRow>
          <FieldRow label="כותרת מודגשת (כתום)"><Input value={section.title_accent || ""} onChange={(e) => onChange({ title_accent: e.target.value })} className="h-10" /></FieldRow>
          <FieldRow label="פסקה (אפשר **טקסט מודגש**)"><Textarea value={section.body || ""} onChange={(e) => onChange({ body: e.target.value })} rows={5} /></FieldRow>
        </>
      );
    case "questionnaire":
      return (
        <>
          <FieldRow label="Eyebrow"><Input value={section.eyebrow || ""} onChange={(e) => onChange({ eyebrow: e.target.value })} className="h-10" /></FieldRow>
          <FieldRow label="כותרת"><Input value={section.title || ""} onChange={(e) => onChange({ title: e.target.value })} className="h-10" /></FieldRow>
          <FieldRow label="אינטרו"><Textarea value={section.intro || ""} onChange={(e) => onChange({ intro: e.target.value })} rows={3} /></FieldRow>
          <FieldRow label="בולטים (אחד בכל שורה)">
            <Textarea
              value={(section.bullets || []).join("\n")}
              onChange={(e) => onChange({ bullets: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })}
              rows={4}
              placeholder="להבין מה באמת חונק…"
            />
          </FieldRow>
          <FieldRow label="כותרת ה-Sidebar"><Input value={section.sidebar_title || ""} onChange={(e) => onChange({ sidebar_title: e.target.value })} className="h-10" /></FieldRow>
          <FieldRow label="טקסט ה-Sidebar"><Textarea value={section.sidebar_text || ""} onChange={(e) => onChange({ sidebar_text: e.target.value })} rows={4} /></FieldRow>
        </>
      );
    case "stats":
      return (
        <p className="text-xs text-stone-500">סקשן זה מציג את הנתונים שהוגדרו ברשימת "נתונים / סטטיסטיקות" למעלה. אין כאן טקסטים לעריכה — רק האם הסקשן מוצג ובאיזה סדר.</p>
      );
    case "bonus":
      return (
        <>
          <FieldRow label="תווית (Badge)"><Input value={section.badge || ""} onChange={(e) => onChange({ badge: e.target.value })} className="h-10" /></FieldRow>
          <FieldRow label="כותרת — חלק קבוע"><Input value={section.title_prefix || ""} onChange={(e) => onChange({ title_prefix: e.target.value })} className="h-10" /></FieldRow>
          <FieldRow label="כותרת — חלק מודגש (כתום)"><Input value={section.title_accent || ""} onChange={(e) => onChange({ title_accent: e.target.value })} className="h-10" /></FieldRow>
          <FieldRow label="פסקה"><Textarea value={section.body || ""} onChange={(e) => onChange({ body: e.target.value })} rows={4} /></FieldRow>
          <FieldRow label="צ'יפים (אחד בכל שורה, כולל סימן ✓ אם רוצים)">
            <Textarea
              value={(section.chips || []).join("\n")}
              onChange={(e) => onChange({ chips: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })}
              rows={3}
              placeholder="✓ קהילה תומכת"
            />
          </FieldRow>
        </>
      );
    case "final_cta":
      return (
        <>
          <FieldRow label="כותרת"><Input value={section.title || ""} onChange={(e) => onChange({ title: e.target.value })} className="h-10" /></FieldRow>
          <FieldRow label="תת-כותרת"><Textarea value={section.subtitle || ""} onChange={(e) => onChange({ subtitle: e.target.value })} rows={2} /></FieldRow>
          <p className="text-xs text-stone-400">* טקסט ה-CTA עצמו נערך כ"טקסט CTA תחתון" בכרטיסיית Hero.</p>
        </>
      );
    default:
      return null;
  }
}

function FieldRow({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-bold mb-1 block text-stone-600">{label}</Label>
      {children}
    </div>
  );
}
